import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Divider, Skeleton, Stack, Tab, Typography } from '@mui/material';
import { UrlParams } from '@provision/portal-routes';
import { useCallback, useState } from 'react';
import { useOriginalEmail } from 'src/graphql/entities/original-email';
import { useRouteParams } from 'src/utils/routing';

type TabName = 'html' | 'text' | 'source';

const CustomTabPanel = (props: { value: TabName; children: React.ReactNode }) => (
  <TabPanel value={props.value} sx={{ overflowY: 'auto', pb: 2, width: '100%' }}>
    {props.children}
  </TabPanel>
);

export const OriginalEmail = () => {
  const { conversationMessageId } = useRouteParams(UrlParams.conversationsMessageId);
  const { originalEmailInfo, loading } = useOriginalEmail(conversationMessageId);
  const [tabName, setTabName] = useState<TabName>('html');

  const handleTabChange = useCallback(
    (_: unknown, value: TabName) => setTabName(value),
    [setTabName],
  );

  return (
    <Stack height="100%" alignItems="flex-start" justifyContent="start">
      <Box display="flex" flexDirection="column" gap={1.5} padding={3}>
        <Box display="flex">
          <Typography fontWeight="bold" minWidth="4em" mr={3} align="right">
            From:
          </Typography>
          <Typography>
            {loading ? <Skeleton width="10em" /> : originalEmailInfo?.from}
          </Typography>
        </Box>

        <Box display="flex">
          <Typography fontWeight="bold" minWidth="4em" mr={3} align="right">
            To:
          </Typography>
          <Typography>
            {loading ? <Skeleton width="10em" /> : originalEmailInfo?.to}
          </Typography>
        </Box>

        {originalEmailInfo?.cc?.trim() ? (
          <Box display="flex">
            <Typography fontWeight="bold" minWidth="4em" mr={3} align="right">
              CC:
            </Typography>
            <Typography>
              {loading ? <Skeleton width="10em" /> : originalEmailInfo?.cc}
            </Typography>
          </Box>
        ) : null}

        <Box display="flex">
          <Typography fontWeight="bold" minWidth="4em" mr={3} align="right">
            Subject:
          </Typography>
          <Typography>
            {loading ? <Skeleton width="10em" /> : originalEmailInfo?.subject}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ width: '100%' }} />

      <TabContext value={tabName}>
        <Box sx={{ ml: 0 }}>
          <TabList onChange={handleTabChange}>
            <Tab label="HTML" value={'html' as TabName} />
            <Tab label="Text" value={'text' as TabName} />
            <Tab label="Source" value={'source' as TabName} />
          </TabList>
        </Box>
        <Divider sx={{ width: '100%' }} />

        {loading ? (
          <Skeleton width="30em" height="10em" sx={{ ml: 2 }} />
        ) : (
          <>
            <CustomTabPanel value={'html' as TabName}>
              <div
                dangerouslySetInnerHTML={{
                  // There are cases when email doesn't have HTML, and has only raw text.
                  // In cases like that `parsedEmail.html` is empty, and it leaves users confused.
                  // Therefore, if there is no HTML we just show text.
                  __html: originalEmailInfo?.html || originalEmailInfo?.text || '',
                }}
              />
            </CustomTabPanel>
            <CustomTabPanel value={'text' as TabName}>
              <Box component="pre" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {originalEmailInfo?.text}
              </Box>
            </CustomTabPanel>
            <CustomTabPanel value={'source' as TabName}>
              <Box component="pre" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                {originalEmailInfo?.source}
              </Box>
            </CustomTabPanel>
          </>
        )}
      </TabContext>
    </Stack>
  );
};
